import React, { useEffect, useState } from 'react';
import WorkOrders from '../../models/WorkOrders';
import { useFilterContext } from '../../context/SharedFilterContext';
import DataTable from 'react-data-table-component';
import { ImSpinner3 } from 'react-icons/im';
import { FaExternalLinkAlt } from 'react-icons/fa';

const WorkOrdersTable = ({ setShowWorkOrdersTable }) => {
  const [corrigoWorkOrders, setCorrigoWorkOrders] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const { selectedProperty } = useFilterContext();

  useEffect(() => {
    let isMounted = true;

    const fetchWorkOrders = async () => {
      if (!selectedProperty || !selectedProperty.external_uuid) {
        console.warn("[WorkOrdersTable] No selectedProperty or external_uuid provided.");
        setLoading(false);
        return;
      }

      console.log("[WorkOrdersTable] Fetching work orders for property:", selectedProperty.external_uuid);

      try {
        let workOrders = await WorkOrders.getCorrigoWorkOrdersByProperty(selectedProperty.external_uuid);
        console.log("[WorkOrdersTable] This is the external_uuid we are using:", selectedProperty.external_uuid);
        console.log("[WorkOrdersTable] Corrigo Work Orders fetched successfully:", workOrders);

        // Filter out work orders with source 'workzone_area_match_historical_context'
        // workOrders = workOrders.filter(order => order.source !== 'workzone_area_match_historical_context');
        // console.log("[WorkOrdersTable] Number of work orders after filtering:", workOrders.length);

        if (isMounted) {
          setCorrigoWorkOrders(workOrders || []);
        }
      } catch (err) {
        console.error("[WorkOrdersTable] Error fetching work orders:", err);
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchWorkOrders();

    return () => {
      isMounted = false;
    };
  }, [selectedProperty]);

  // Helper function to safely parse dates for sorting
  const sortDate = (rowA, rowB, selector) => {
    const dateA = rowA[selector] ? new Date(rowA[selector]) : null;
    const dateB = rowB[selector] ? new Date(rowB[selector]) : null;
    
    // Handle null/invalid dates
    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;
    
    // Compare valid dates
    return dateA.getTime() - dateB.getTime();
  };

  const allColumns = [
    {
      name: 'Corrigo Link',
      width: '100px',
      id: 'corrigo_link',
      cell: row => (
        <a
          href={`https://enterprise.corrigo.com/corpnet/workorder/workorderdetails.aspx/${row.work_order_id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: '#0066cc',
            textDecoration: 'none',
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f7ff'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title={`View Work Order ${row.work_order_id} in Corrigo Enterprise`}
        >
          View <FaExternalLinkAlt size={12} />
        </a>
      )
    },
    { 
        name: 'DT Created', 
        selector: row => row.dt_created, 
        sortable: true, 
        width: '150px', 
        wrap: true,
        sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_created'),
        id: 'dt_created'
      },
    { name: 'Work Order ID', selector: row => row.work_order_id, sortable: true, width: '150px', wrap: true, id: 'work_order_id' },
    { name: 'Corrigo Table ID', selector: row => row.id, sortable: true, width: '80px', wrap: true, id: 'id' },
    { name: 'Corrigo Table UUID', selector: row => row.uuid, sortable: true, width: '200px', wrap: true, id: 'uuid' },
    { name: 'Number', selector: row => row.number, sortable: true, width: '100px', wrap: true, id: 'number' },
    { name: 'X Number', selector: row => row.x_number, sortable: true, width: '100px', wrap: true, id: 'x_number' },
    { name: 'Type Category', selector: row => row.type_category, sortable: true, width: '150px', wrap: true, id: 'type_category' },
    { name: 'Work Order Cost ID', selector: row => row.work_order_cost_id, sortable: true, width: '150px', wrap: true, id: 'work_order_cost_id' },
    { name: 'Priority ID', selector: row => row.priority_id, sortable: true, width: '100px', wrap: true, id: 'priority_id' },
    { name: 'Priority', selector: row => row.priority, sortable: true, width: '100px', wrap: true, id: 'priority' },
    { name: 'Requestor Contact ID', selector: row => row.requestor_contact_id, sortable: true, width: '150px', wrap: true, id: 'requestor_contact_id' },
    { name: 'Access', selector: row => row.access, sortable: true, width: '100px', wrap: true, id: 'access' },
    { name: 'Main Asset ID', selector: row => row.main_asset_id, sortable: true, width: '150px', wrap: true, id: 'main_asset_id' },
    { name: 'Short Location', selector: row => row.short_location, sortable: true, width: '150px', wrap: true, id: 'short_location' },
    { name: 'Task Refinement', selector: row => row.task_refinement, sortable: true, width: '150px', wrap: true, id: 'task_refinement' },
    { name: 'Work Zone ID', selector: row => row.work_zone_id, sortable: true, width: '150px', wrap: true, id: 'work_zone_id' },
    { name: 'Employee ID', selector: row => row.employee_id, sortable: true, width: '150px', wrap: true, id: 'employee_id' },
    { name: 'Duration', selector: row => row.duration, sortable: true, width: '100px', wrap: true, id: 'duration' },
    { name: 'Address ID', selector: row => row.address_id, sortable: true, width: '150px', wrap: true, id: 'address_id' },
    { name: 'Bill To Type', selector: row => row.bill_to_type, sortable: true, width: '150px', wrap: true, id: 'bill_to_type' },
    { name: 'Bill To Option ID', selector: row => row.bill_to_option_id, sortable: true, width: '150px', wrap: true, id: 'bill_to_option_id' },
    { name: 'Vendor NTE Currency', selector: row => row.vendor_nte_currency, sortable: true, width: '150px', wrap: true, id: 'vendor_nte_currency' },
    { name: 'Vendor NTE Value', selector: row => row.vendor_nte_value, sortable: true, width: '150px', wrap: true, id: 'vendor_nte_value' },
    { name: 'Specialty ID', selector: row => row.specialty_id, sortable: true, width: '150px', wrap: true, id: 'specialty_id' },
    { name: 'PO Number', selector: row => row.po_number, sortable: true, width: '150px', wrap: true, id: 'po_number' },
    { name: 'Sub Type ID', selector: row => row.sub_type_id, sortable: true, width: '150px', wrap: true, id: 'sub_type_id' },
    { name: 'Repair Code ID', selector: row => row.repair_code_id, sortable: true, width: '150px', wrap: true, id: 'repair_code_id' },
    { name: 'Customer ID', selector: row => row.customer_id, sortable: true, width: '150px', wrap: true, id: 'customer_id' },
    { name: 'Customer NTE Currency', selector: row => row.customer_nte_currency, sortable: true, width: '150px', wrap: true, id: 'customer_nte_currency' },
    { name: 'Customer NTE Value', selector: row => row.customer_nte_value, sortable: true, width: '150px', wrap: true, id: 'customer_nte_value' },
    { name: 'Billing Rule', selector: row => row.billing_rule, sortable: true, width: '150px', wrap: true, id: 'billing_rule' },
    { name: 'Costs Total Value', selector: row => row.costs_total_value, sortable: true, width: '150px', wrap: true, id: 'costs_total_value' },
    { name: 'Costs Total Currency', selector: row => row.costs_total_currency, sortable: true, width: '150px', wrap: true, id: 'costs_total_currency' },
    { name: 'Vendor Invoice Total Value', selector: row => row.vendor_invoice_total_value, sortable: true, width: '150px', wrap: true, id: 'vendor_invoice_total_value' },
    { name: 'Vendor Invoice Total Currency', selector: row => row.vendor_invoice_total_currency, sortable: true, width: '150px', wrap: true, id: 'vendor_invoice_total_currency' },
    { name: 'Expenses Total Value', selector: row => row.expenses_total_value, sortable: true, width: '150px', wrap: true, id: 'expenses_total_value' },
    { name: 'Expenses Total Currency', selector: row => row.expenses_total_currency, sortable: true, width: '150px', wrap: true, id: 'expenses_total_currency' },
    { name: 'Service Fees Total Value', selector: row => row.service_fees_total_value, sortable: true, width: '150px', wrap: true, id: 'service_fees_total_value' },
    { name: 'Service Fees Total Currency', selector: row => row.service_fees_total_currency, sortable: true, width: '150px', wrap: true, id: 'service_fees_total_currency' },
    { name: 'Contact Name', selector: row => row.contact_name, sortable: true, width: '150px', wrap: true, id: 'contact_name' },
    { name: 'Contact Address ID', selector: row => row.contact_address_id, sortable: true, width: '150px', wrap: true, id: 'contact_address_id' },
    { name: 'WON ID', selector: row => row.won_id, sortable: true, width: '150px', wrap: true, id: 'won_id' },
    { name: 'DT Scheduled Start', selector: row => row.dt_scheduled_start, sortable: true, width: '150px', wrap: true, id: 'dt_scheduled_start' },
    { name: 'Scheduled Start UTC', selector: row => row.scheduled_start_utc, sortable: true, width: '150px', wrap: true, id: 'scheduled_start_utc' },
    { name: 'Is Warranty', selector: row => row.is_warranty ? 'Yes' : 'No', sortable: true, width: '100px', wrap: true, id: 'is_warranty' },
    { name: 'Flag ID', selector: row => row.flag_id, sortable: true, width: '100px', wrap: true, id: 'flag_id' },
    { name: 'Time Zone', selector: row => row.time_zone, sortable: true, width: '100px', wrap: true, id: 'time_zone' },
    { name: 'Currency Type ID', selector: row => row.currency_type_id, sortable: true, width: '150px', wrap: true, id: 'currency_type_id' },
    { name: 'Bill Status', selector: row => row.bill_status, sortable: true, width: '150px', wrap: true, id: 'bill_status' },
    { name: 'AP State ID', selector: row => row.ap_state_id, sortable: true, width: '150px', wrap: true, id: 'ap_state_id' },
    { name: 'AP Status ID', selector: row => row.ap_status_id, sortable: true, width: '150px', wrap: true, id: 'ap_status_id' },
    { name: 'Is Pre Billed', selector: row => row.is_pre_billed ? 'Yes' : 'No', sortable: true, width: '100px', wrap: true, id: 'is_pre_billed' },
    { name: 'Cost State', selector: row => row.cost_state, sortable: true, width: '150px', wrap: true, id: 'cost_state' },
    { name: 'Cost Status ID', selector: row => row.cost_status_id, sortable: true, width: '150px', wrap: true, id: 'cost_status_id' },
    { name: 'Job Code', selector: row => row.job_code, sortable: true, width: '150px', wrap: true, id: 'job_code' },
    { name: 'Authorization Code', selector: row => row.authorization_code, sortable: true, width: '150px', wrap: true, id: 'authorization_code' },
    { name: 'Check Number', selector: row => row.check_number, sortable: true, width: '150px', wrap: true, id: 'check_number' },
    { name: 'Payment Amount Value', selector: row => row.payment_amount_value, sortable: true, width: '150px', wrap: true, id: 'payment_amount_value' },
    { name: 'Payment Amount Currency', selector: row => row.payment_amount_currency, sortable: true, width: '150px', wrap: true, id: 'payment_amount_currency' },
    { name: 'Payment Notes', selector: row => row.payment_notes, sortable: true, width: '150px', wrap: true, id: 'payment_notes' },
    { name: 'Check Cut Date', selector: row => row.check_cut_date, sortable: true, width: '150px', wrap: true, id: 'check_cut_date' },
    { name: 'Check Clear Date', selector: row => row.check_clear_date, sortable: true, width: '150px', wrap: true, id: 'check_clear_date' },
    { name: 'Vendor Invoice Date', selector: row => row.vendor_invoice_date, sortable: true, width: '150px', wrap: true, id: 'vendor_invoice_date' },
    { name: 'Vendor Invoice Received Date', selector: row => row.vendor_invoice_received_date, sortable: true, width: '150px', wrap: true, id: 'vendor_invoice_received_date' },
    { name: 'WO Items Sort Order IDX', selector: row => row.wo_items_sort_order_idx, sortable: true, width: '150px', wrap: true, id: 'wo_items_sort_order_idx' },
    { name: 'WO Items Asset Location', selector: row => row.wo_items_asset_location, sortable: true, width: '150px', wrap: true, id: 'wo_items_asset_location' },
    { name: 'WO Items Asset ID', selector: row => row.wo_items_asset_id, sortable: true, width: '150px', wrap: true, id: 'wo_items_asset_id' },
    { name: 'WO Items Task ID', selector: row => row.wo_items_task_id, sortable: true, width: '150px', wrap: true, id: 'wo_items_task_id' },
    { name: 'WO Items Comment', selector: row => row.wo_items_comment, sortable: true, width: '150px', wrap: true, id: 'wo_items_comment' },
    { name: 'Equipment Asset ID', selector: row => row.equipment_asset_id, sortable: true, width: '150px', wrap: true, id: 'equipment_asset_id' },
    { name: 'Equipment Comment', selector: row => row.equipment_comment, sortable: true, width: '150px', wrap: true, id: 'equipment_comment' },
    { name: 'Equipment Refrigerant Used', selector: row => row.equipment_refrigerant_used, sortable: true, width: '150px', wrap: true, id: 'equipment_refrigerant_used' },
    { name: 'Equipment Type', selector: row => row.equipment_type, sortable: true, width: '150px', wrap: true, id: 'equipment_type' },
    { name: 'Procedure Template ID', selector: row => row.procedure_template_id, sortable: true, width: '150px', wrap: true, id: 'procedure_template_id' },
    { name: 'Procedure Asset ID', selector: row => row.procedure_asset_id, sortable: true, width: '150px', wrap: true, id: 'procedure_asset_id' },
    { name: 'Procedure Type', selector: row => row.procedure_type, sortable: true, width: '150px', wrap: true, id: 'procedure_type' },
    { name: 'Procedure Allow Mark All', selector: row => row.procedure_allow_mark_all, sortable: true, width: '150px', wrap: true, id: 'procedure_allow_mark_all' },
    { name: 'Procedure Allow Not Applicable', selector: row => row.procedure_allow_not_applicable, sortable: true, width: '150px', wrap: true, id: 'procedure_allow_not_applicable' },
    { name: 'Procedure Is Not Applicable', selector: row => row.procedure_is_not_applicable, sortable: true, width: '150px', wrap: true, id: 'procedure_is_not_applicable' },
    { name: 'Procedure Flag ID', selector: row => row.procedure_flag_id, sortable: true, width: '150px', wrap: true, id: 'procedure_flag_id' },
    { name: 'Procedure Status ID', selector: row => row.procedure_status_id, sortable: true, width: '150px', wrap: true, id: 'procedure_status_id' },
    { name: 'Procedure Index', selector: row => row.procedure_index, sortable: true, width: '150px', wrap: true, id: 'procedure_index' },
    { name: 'Financial Items', selector: row => row.financial_items, sortable: true, width: '150px', wrap: true, id: 'financial_items' },
    { name: 'Quote Items', selector: row => row.quote_items, sortable: true, width: '150px', wrap: true, id: 'quote_items' },
    { name: 'Estimate Items', selector: row => row.estimate_items, sortable: true, width: '150px', wrap: true, id: 'estimate_items' },
    { name: 'Action Logs', selector: row => row.action_logs, sortable: true, width: '150px', wrap: true, id: 'action_logs' },
    { name: 'Last Action Date', selector: row => row.last_action_date, sortable: true, width: '150px', wrap: true, id: 'last_action_date' },
    { 
      name: 'DT Due', 
      selector: row => row.dt_due, 
      sortable: true, 
      width: '150px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_due'),
      id: 'dt_due'
    },
    { name: 'DT Acknowledge By', selector: row => row.dt_acknowledge_by, sortable: true, width: '150px', wrap: true, id: 'dt_acknowledge_by' },
    { name: 'Acknowledge By UTC', selector: row => row.acknowledge_by_utc, sortable: true, width: '150px', wrap: true, id: 'acknowledge_by_utc' },
    { name: 'Last Action Date UTC', selector: row => row.last_action_date_utc, sortable: true, width: '150px', wrap: true, id: 'last_action_date_utc' },
    { name: 'Created Date UTC', selector: row => row.created_date_utc, sortable: true, width: '150px', wrap: true, id: 'created_date_utc' },
    { name: 'Due Date UTC', selector: row => row.due_date_utc, sortable: true, width: '150px', wrap: true, id: 'due_date_utc' },
    { name: 'DT On Site By', selector: row => row.dt_on_site_by, sortable: true, width: '150px', wrap: true, id: 'dt_on_site_by' },
    { name: 'DT UTC On Site By', selector: row => row.dt_utc_on_site_by, sortable: true, width: '150px', wrap: true, id: 'dt_utc_on_site_by' },
    { name: 'Last Action ID', selector: row => row.last_action_id, sortable: true, width: '150px', wrap: true, id: 'last_action_id' },
    { name: 'Induction and Safety Status ID', selector: row => row.induction_and_safety_status_id, sortable: true, width: '150px', wrap: true, id: 'induction_and_safety_status_id' },
    { name: 'Concurrency ID', selector: row => row.concurrency_id, sortable: true, width: '150px', wrap: true, id: 'concurrency_id' },
    { name: 'Owner ID', selector: row => row.owner_id, sortable: true, width: '150px', wrap: true, id: 'owner_id' },
    { name: 'Evaluation IDs While Open', selector: row => row.evaluation_ids_while_open, sortable: true, width: '150px', wrap: true, id: 'evaluation_ids_while_open' },
    { name: 'Latest Evaluation ID After Closed', selector: row => row.latest_evaluation_id_after_closed, sortable: true, width: '150px', wrap: true, id: 'latest_evaluation_id_after_closed' },
    { name: 'Source Company ID', selector: row => row.source_company_id, sortable: true, width: '150px', wrap: true, id: 'source_company_id' },
    { name: 'Note Text', selector: row => row.note_text, sortable: true, width: '150px', wrap: true, id: 'note_text' },
    { name: 'Completion Note Text', selector: row => row.completion_note_text, sortable: true, width: '150px', wrap: true, id: 'completion_note_text' },
    { name: 'Created At', selector: row => row.created_at, sortable: true, width: '150px', wrap: true, id: 'created_at' },
    { 
      name: 'Updated At', 
      selector: row => row.updated_at, 
      sortable: true, 
      width: '150px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'updated_at'),
      id: 'updated_at'
    },
    { name: 'Property Number', selector: row => row.property_number, sortable: true, width: '150px', wrap: true, id: 'property_number' },
    { name: 'Assignee Screen Name', selector: row => row.assignee_screen_name, sortable: true, width: '150px', wrap: true, id: 'assignee_screen_name' },
    { name: 'Failure Code', selector: row => row.failure_code, sortable: true, width: '150px', wrap: true, id: 'failure_code' },
    { name: 'Created By Name', selector: row => row.created_by_name, sortable: true, width: '150px', wrap: true, id: 'created_by_name' },
    { name: 'Subtype Name', selector: row => row.subtype_name, sortable: true, width: '150px', wrap: true, id: 'subtype_name' },
    { name: 'Failure Category', selector: row => row.failure_category, sortable: true, width: '150px', wrap: true, id: 'failure_category' },
    { name: 'Status ID', selector: row => row.status_id, sortable: true, width: '100px', wrap: true, id: 'status_id' },

    { name: 'Total Spot Cost', selector: row => row.total_spot_cost, sortable: true, width: '150px', wrap: true, id: 'total_spot_cost' },
    { 
      name: 'Is DST', 
      selector: row => row.is_dst ? 'Yes' : 'No', 
      sortable: true, 
      width: '100px', 
      wrap: true, 
      id: 'is_dst' 
    }
  ].map(column => ({
    ...column,
    wrap: true
  }));
  
  // Define the DST columns with specific ordering and widths
  const dstColumns = [
    {
      name: 'Corrigo Link',
      width: '100px',
      id: 'corrigo_link',
      cell: row => (
        <a
          href={`https://enterprise.corrigo.com/corpnet/workorder/workorderdetails.aspx/${row.work_order_id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: '#0066cc',
            textDecoration: 'none',
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f7ff'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title={`View Work Order ${row.work_order_id} in Corrigo Enterprise`}
        >
          View <FaExternalLinkAlt size={12} />
        </a>
      )
    },
    { 
      name: 'DT Created', 
      selector: row => row.dt_created, 
      sortable: true, 
      width: '180px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_created'),
      id: 'dt_created'
    },
    { name: 'Number', selector: row => row.number, sortable: true, width: '150px', wrap: true, id: 'number' },

    { name: 'DST Equipment', selector: row => row.root_comp, sortable: true, width: '200px', wrap: true, id: 'fig_root_comp' },
    { name: 'Task Refinement', selector: row => row.task_refinement, sortable: true, width: '600px', wrap: true, id: 'task_refinement' },
    { name: 'Assignee Screen Name', selector: row => row.assignee_screen_name, sortable: true, width: '150px', wrap: true, id: 'assignee_screen_name' },
    { 
      name: 'DT Due', 
      selector: row => row.dt_due, 
      sortable: true, 
      width: '180px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_due'),
      id: 'dt_due'
    },
    { name: 'Subtype Name', selector: row => row.subtype_name, sortable: true, width: '150px', wrap: true, id: 'subtype_name' },
    { name: 'Completion Note Text', selector: row => row.completion_note_text, sortable: true, width: '600px', wrap: true, id: 'completion_note_text' },
    { 
      name: 'Updated At', 
      selector: row => row.updated_at, 
      sortable: true, 
      width: '180px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'updated_at'),
      id: 'updated_at'
    },
    { name: 'Failure Category', selector: row => row.failure_category, sortable: true, width: '150px', wrap: true, id: 'failure_category' },
    { name: 'Status ID', selector: row => row.status_id, sortable: true, width: '100px', wrap: true, id: 'status_id' },

    { name: 'Costs Total Value', selector: row => row.costs_total_value, sortable: true, width: '150px', wrap: true, id: 'costs_total_value' },
    { name: 'Expenses Total Value', selector: row => row.expenses_total_value, sortable: true, width: '170px', wrap: true, id: 'expenses_total_value' },
    { name: 'Service Fees Total Value', selector: row => row.service_fees_total_value, sortable: true, width: '170px', wrap: true, id: 'service_fees_total_value' },
    { 
      name: 'Is DST', 
      selector: row => row.is_dst ? 'Yes' : 'No', 
      sortable: true, 
      width: '100px', 
      wrap: true, 
      id: 'is_dst' 
    }
  ];

  if (loading) {
    return <ImSpinner3 className="icon-spin" style={{ fontSize: 50, margin: 'auto', display: 'block' }} />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'left', color: 'red' }}>
        <h2 style={{ fontWeight: 'bold', marginBottom: '20px' }}>All Work Orders</h2>
        <p style={{ fontSize: '18px' }}>Currently facing issues gathering work orders</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontWeight: 'bold', textAlign: 'left', margin: 0 }}>
          Work Orders for Property: {selectedProperty?.name || 'N/A'} ({selectedProperty?.external_uuid || 'N/A'})
        </h2>
        <button 
          onClick={() => setShowAllColumns(!showAllColumns)} 
          style={{
            padding: '8px 16px',
            backgroundColor: '#f0f0f0',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            marginLeft: '15px'
          }}
        >
          {showAllColumns ? 'Showing All' : 'Showing DST Work Orders'}
        </button>
      </div>

      <DataTable
        columns={showAllColumns ? allColumns : dstColumns}
        data={corrigoWorkOrders}
        noDataComponent="No Corrigo Work Orders found."
        responsive
        highlightOnHover
        persistTableHead
        defaultSortFieldId="dt_created"
        defaultSortAsc={false}
        style={{ border: '1px solid #ddd', borderRadius: '8px' }}
        customStyles={{
          headCells: {
            style: {
              backgroundColor: '#e3e3e3',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
            },
          },
          cells: {
            style: {
              padding: '10px',
              fontSize: '13px',
              color: '#555',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word'
            },
          },
        }}
      />
    </div>
  );
};

export default WorkOrdersTable;
